import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clientsAPI } from '../utils/api';
import { format } from 'date-fns';
import { FiEdit2, FiFileText, FiArrowLeft, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import './ClientDetail.css';

const ClientDetail = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const [clientRes, invoicesRes, statsRes] = await Promise.all([
        clientsAPI.getById(id),
        clientsAPI.getInvoices(id, { limit: 10 }),
        clientsAPI.getStats(id),
      ]);
      setClient(clientRes.data.client);
      setInvoices(invoicesRes.data.invoices);
      setStats(statsRes.data.stats);
    } catch (error) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: client?.currency || 'AED',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      paid: 'badge-paid',
      partial: 'badge-partial',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled',
    };
    return statusClasses[status] || 'badge-draft';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Client not found</h3>
          <Link to="/clients" className="btn btn-primary">
            <FiArrowLeft /> Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <Link to="/clients" className="back-link">
            <FiArrowLeft /> Back to Clients
          </Link>
          <h1>{client.company_name}</h1>
          <p>{client.contact_person}</p>
        </div>
        <Link to={`/invoices/create?clientId=${client.id}`} className="btn btn-primary">
          <FiFileText /> New Invoice
        </Link>
      </div>

      <div className="client-detail-grid">
        <div className="client-info-section">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Client Information</h3>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Company Name</span>
                  <span className="info-value">{client.company_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Contact Person</span>
                  <span className="info-value">{client.contact_person}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">
                    <a href={`mailto:${client.email}`}>
                      <FiMail /> {client.email}
                    </a>
                  </span>
                </div>
                {client.phone && (
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <span className="info-value">
                      <a href={`tel:${client.phone}`}>
                        <FiPhone /> {client.phone}
                      </a>
                    </span>
                  </div>
                )}
                {client.address && (
                  <div className="info-item">
                    <span className="info-label">Address</span>
                    <span className="info-value">
                      <FiMapPin /> {client.address}
                      {client.city && `, ${client.city}`}
                      {client.country && `, ${client.country}`}
                    </span>
                  </div>
                )}
                {client.trn && (
                  <div className="info-item">
                    <span className="info-label">TRN</span>
                    <span className="info-value">{client.trn}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Payment Terms</span>
                  <span className="info-value">{client.payment_terms || 30} days</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Currency</span>
                  <span className="info-value">{client.currency || 'AED'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="client-stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-value">{stats?.total_invoices || 0}</div>
              <div className="stat-card-label">Total Invoices</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{formatCurrency(stats?.total_amount)}</div>
              <div className="stat-card-label">Total Amount</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{formatCurrency(stats?.paid_amount)}</div>
              <div className="stat-card-label">Paid</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{formatCurrency(stats?.outstanding_amount)}</div>
              <div className="stat-card-label">Outstanding</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Invoices</h3>
          <Link to={`/invoices?clientId=${client.id}`} className="btn btn-sm btn-secondary">
            View All
          </Link>
        </div>
        <div className="card-body">
          {invoices.length === 0 ? (
            <div className="empty-state">
              <p>No invoices yet</p>
              <Link to={`/invoices/create?clientId=${client.id}`} className="btn btn-primary">
                Create Invoice
              </Link>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <Link to={`/invoices/${invoice.id}`} className="text-primary font-medium">
                          {invoice.invoice_number}
                        </Link>
                      </td>
                      <td>{formatDate(invoice.issue_date)}</td>
                      <td>{formatDate(invoice.due_date)}</td>
                      <td>{formatCurrency(invoice.total)}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;